import React from 'react';
import {
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@material-ui/core';
import { Button } from '#root/components/ui';
import { Rating } from '@material-ui/lab';
import { IconEdit } from '@tabler/icons';
import { format, isValid, compareAsc, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import clsx from 'clsx';

import { shopperService, travelerService } from '#root/services';
import { GuveryDialog } from '#root/ui-components';
import { useOptimisticAction } from '#root/hooks/useOptimisticAction';
import styles from './ratingDialog.module.scss';

const labels: { [index: string]: string } = {
  0.5: 'Muy Malo',
  1: 'Muy Malo',
  1.5: 'Malo',
  2: 'Malo',
  2.5: 'Regular',
  3: 'Regular',
  3.5: 'Bueno',
  4: 'Bueno',
  4.5: 'Excelente',
  5: 'Excelente',
};

const getMinorDate = (date1, date2) => {
  if (!date1) {
    return date2;
  }
  if (!date2) {
    return date1;
  }
  if (!isValid(date1) || !isValid(date2)) {
    return null;
  }
  const replaceDate1 = date1?.replace(/ /g, 'T');
  const replaceDate2 = date2?.replace(/ /g, 'T');

  const validDate1 = new Date(replaceDate1);
  const validDate2 = new Date(replaceDate2);

  const result = compareAsc(validDate1, validDate2);

  if (result === 1) {
    return date2;
  } else {
    return date1;
  }
};

const formatTime = (date) => {
  if (!isValid(parseISO(date))) {
    return '...';
  }
  const replaceDate = date?.replace(/ /g, 'T');
  const validDate = new Date(replaceDate);
  return format(validDate, "dd'/'MM'/'YYY", { locale: es });
};

const RatingDialog = (props) => {
  const {
    id,
    ratingId,
    open = false,
    onClose,
    ratingType = 'shopper',
    title,
    date1,
    date2,
    hint,
    userData,
    handleLoading,
    createMessage,
    confirmText,
  } = props;
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [score, setScore] = React.useState<number | null>(0);
  const [hover, setHover] = React.useState(-1);
  const [opinion, setOpinion] = React.useState('');

  const time = getMinorDate(date1, date2);
  const displayTime = time ? formatTime(time) : '...';

  // Hook para manejar calificación con prevención de duplicados
  const ratingAction = useOptimisticAction({
    action: async (data) => {
      if (ratingType === 'shopper') {
        return await shopperService.dealRating(data);
      }
      if (ratingType === 'traveler') {
        return await travelerService.dealRating(data);
      }
      throw new Error('Tipo de calificación no válido');
    },
    onSuccess: () => {
      handleLoading();
      onClose();
    },
    options: {
      showMessages: true,
      messages: {
        success: 'Gracias por tu calificación',
        error: (error) => error?.message || 'No se pudo enviar la calificación',
      },
    },
  });

  const handleExpandedChange = (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(true);
  };

  const handleOpinionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpinion(event.target.value);
  };

  const handleSend = () => {
    const arg = {
      deal_id: id,
      rating_id: ratingId,
      score: score,
      opinion: opinion,
    };
    ratingAction.execute(arg).catch(console.error);
  };

  return (
    <GuveryDialog disableActions content={false} title={title} open={open} onClose={onClose}>
      <div className={styles.root}>
        <div className={styles.wrap}>
          <div className={styles.user}>
            <Avatar className={styles.avatar} src={userData?.picture_path}></Avatar>
            <h4 className={styles.name}>{userData?.one_fullname}</h4>
          </div>
          <p className={styles.desc}>
            Por el acuerdo #{id}, entregado el {displayTime} aprox.
          </p>
          <div className={styles.score}>
            <Rating
              className={styles.rating}
              name="score"
              size="large"
              value={score}
              onChange={(event, newValue) => {
                setScore(newValue);
              }}
              onChangeActive={(event, newHover) => {
                setHover(newHover);
              }}
            />
            {score !== null && (
              <div className={styles.text}>{labels[hover !== -1 ? hover : score]}</div>
            )}
          </div>
          <div className={styles.form}>
            <Accordion
              className={clsx(styles.accordion, { [styles.expanded]: expanded })}
              expanded={expanded}
              onChange={handleExpandedChange}>
              <AccordionSummary
                className={clsx(styles.summary)}
                expandIcon={!expanded && <IconEdit />}
                aria-controls="panel1a-content"
                id="panel1a-header">
                <h6 className={styles.heading}>Escribe una reseña (opcional)</h6>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  inputRef={(input) => input && input.focus()}
                  className={styles.field}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="..."
                  value={opinion}
                  onChange={handleOpinionChange}
                />
              </AccordionDetails>
            </Accordion>
          </div>
          <Button
            fullWidth
            size="lg"
            variant="primary"
            disabled={ratingAction.isPending}
            onClick={handleSend}>
            {confirmText ? confirmText : 'Calificar'}
          </Button>
          {!confirmText && (
            <Button
              fullWidth
              size="lg"
              variant="outline"
              onClick={onClose}
              style={{ marginTop: 16 }}>
              Lo haré después
            </Button>
          )}
          {hint && <span className={styles.hint}>{hint}</span>}
        </div>
      </div>
    </GuveryDialog>
  );
};

export default RatingDialog;
